import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import CampaignsAPI from '../../api/campaigns';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { CAMPAIGNS_EVENTS } from '../../helper/AnalyticsHelper/events';
import { CAMPAIGN_TYPES } from 'shared/constants/campaign';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isCreating: false,
  },
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getCampaigns:
    _state =>
    (campaignType, inboxChannelTypes = null) => {
      let filteredRecords = _state.records.filter(
        record => record.campaign_type === campaignType
      );

      if (inboxChannelTypes && Array.isArray(inboxChannelTypes)) {
        filteredRecords = filteredRecords.filter(record => {
          return (
            record.inbox &&
            inboxChannelTypes.includes(record.inbox.channel_type)
          );
        });
      }

      return filteredRecords.sort((a1, a2) => a1.id - a2.id);
    },
  getSMSCampaigns: (_state, _getters) => {
    const smsChannelTypes = [INBOX_TYPES.SMS, INBOX_TYPES.TWILIO, INBOX_TYPES.AFRICAS_TALKING];
    return _getters.getCampaigns(CAMPAIGN_TYPES.ONE_OFF, smsChannelTypes);
  },
  getWhatsAppCampaigns: (_state, _getters) => {
    const whatsappChannelTypes = [INBOX_TYPES.WHATSAPP];
    return _getters.getCampaigns(CAMPAIGN_TYPES.ONE_OFF, whatsappChannelTypes);
  },
  getEmailCampaigns: (_state, _getters) => {
    const emailChannelTypes = [INBOX_TYPES.EMAIL];
    return _getters.getCampaigns(CAMPAIGN_TYPES.ONE_OFF, emailChannelTypes);
  },
  getWahaCampaigns: _state => {
    return _state.records.filter(record => {
      if (record.campaign_type !== CAMPAIGN_TYPES.ONE_OFF) {
        return false;
      }
      // WAHA inboxes are API channels with waha_session
      return (
        record.inbox?.channel_type === INBOX_TYPES.API &&
        record.inbox?.waha_session
      );
    });
  },
  getLiveChatCampaigns: (_state, _getters) => {
    const liveChatChannelTypes = [INBOX_TYPES.WEB];
    return _getters.getCampaigns(CAMPAIGN_TYPES.ONGOING, liveChatChannelTypes);
  },
  getCustomCampaigns: _state => {
    return _state.records.filter(record => {
      if (record.campaign_type !== CAMPAIGN_TYPES.ONE_OFF) {
        return false;
      }
      const channelType = record.inbox?.channel_type?.toLowerCase() || '';
      const inboxName = record.inbox?.name?.toLowerCase() || '';

      // Exclude SMS, Twilio, AfricasTalking channels
      if (channelType.includes('sms') || channelType.includes('twilio') || channelType.includes('africastalking')) {
        return false;
      }

      // Exclude WhatsApp channels
      if (channelType.includes('whatsapp')) {
        return false;
      }

      // Exclude Email channels
      if (channelType.includes('email')) {
        return false;
      }

      // Exclude Web Widget channels
      if (channelType.includes('web') || channelType.includes('widget')) {
        return false;
      }

      // Exclude inboxes with names ending with _vaha or vaha
      if (inboxName.endsWith('_vaha') || inboxName.endsWith('vaha')) {
        return false;
      }

      return true;
    });
  },
  getAllCampaigns: _state => {
    return _state.records;
  },
};

export const actions = {
  get: async function getCampaigns({ commit }) {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: true });
    try {
      const response = await CampaignsAPI.get();
      commit(types.SET_CAMPAIGNS, response.data);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: false });
    }
  },
  create: async function createCampaign({ commit }, campaignObj) {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: true });
    try {
      const response = await CampaignsAPI.create(campaignObj);
      commit(types.ADD_CAMPAIGN, response.data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: false });
    }
  },
  update: async ({ commit }, { id, ...updateObj }) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: true });
    try {
      const response = await CampaignsAPI.update(id, updateObj);
      AnalyticsHelper.track(CAMPAIGNS_EVENTS.UPDATE_CAMPAIGN);
      commit(types.EDIT_CAMPAIGN, response.data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: false });
    }
  },
  delete: async ({ commit }, id) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isDeleting: true });
    try {
      await CampaignsAPI.delete(id);
      AnalyticsHelper.track(CAMPAIGNS_EVENTS.DELETE_CAMPAIGN);
      commit(types.DELETE_CAMPAIGN, id);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isDeleting: false });
    }
  },
  pause: async ({ commit }, id) => {
    console.log('Store pause action called with id:', id);
    try {
      const response = await CampaignsAPI.pause(id);
      console.log('Pause API response:', response);
      commit(types.EDIT_CAMPAIGN, response.data);
    } catch (error) {
      console.error('Store pause error:', error);
      throw new Error(error);
    }
  },
  resume: async ({ commit }, id) => {
    console.log('Store resume action called with id:', id);
    try {
      const response = await CampaignsAPI.resume(id);
      console.log('Resume API response:', response);
      commit(types.EDIT_CAMPAIGN, response.data);
    } catch (error) {
      console.error('Store resume error:', error);
      throw new Error(error);
    }
  },
  stop: async ({ commit }, id) => {
    console.log('Store stop action called with id:', id);
    try {
      const response = await CampaignsAPI.stop(id);
      console.log('Stop API response:', response);
      commit(types.EDIT_CAMPAIGN, response.data);
    } catch (error) {
      console.error('Store stop error:', error);
      throw new Error(error);
    }
  },
};

export const mutations = {
  [types.SET_CAMPAIGN_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.ADD_CAMPAIGN]: MutationHelpers.create,
  [types.SET_CAMPAIGNS]: MutationHelpers.set,
  [types.EDIT_CAMPAIGN]: MutationHelpers.update,
  [types.DELETE_CAMPAIGN]: MutationHelpers.destroy,
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
